import type { ParseKeys } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiaryEmptyDay } from '@/components/DiaryTable/DiaryEmptyDay';
import { DiaryEvaluations } from '@/components/DiaryTable/DiaryEvaluations';
import { DiaryLessonCell } from '@/components/DiaryTable/DiaryLessonCell';
import {
  CELL_BORDER,
  DAY_CARD,
  HEADER_CELL,
  LESSON_COLUMNS,
  TEXT_CELL_PADDING,
} from '@/components/DiaryTable/columns';
import { DiaryDateRow } from '@/components/DiaryTable/DiaryDateRow';
import { DiaryNotesRow } from '@/components/DiaryTable/DiaryNotesRow';
import { DiaryHomeTasks } from '@/components/DiaryHomeTasks';
import { LessonDetailsModal } from '@/components/LessonDetailsModal';
import { LessonDetailsMode } from '@/types/components';
import { DiaryTopics } from '@/components/DiaryTopics';
import { LessonNumber } from '@/components/DiaryTable/LessonNumber';
import type { DiaryEntry, DiaryLesson } from '@/types/diary';
import { hasEmptySubject, shouldIgnoreLessonClick } from '@/utils/diary';

const COLUMN_HEADER_KEYS: ParseKeys[] = [
  'diary.table.subject',
  'diary.table.topic',
  'diary.table.homework',
  'diary.table.evaluations',
];

interface Props {
  entry: DiaryEntry;
}

export const DiaryTable: React.FC<Props> = ({ entry }) => {
  const { t } = useTranslation();
  const [openLesson, setOpenLesson] = useState<DiaryLesson>();

  const hasLessons = entry.lessons.length > 0;
  const hasNotes = entry.behaviourEntries.length > 0 || entry.notes.length > 0;

  const handleRowClick = (
    event: React.MouseEvent<HTMLDivElement>,
    lesson: DiaryLesson,
  ) => {
    if (hasEmptySubject(lesson) || shouldIgnoreLessonClick(event)) {
      return;
    }

    setOpenLesson(lesson);
  };

  return (
    <div data-date={entry.date} className={`mb-6 scroll-mt-6 ${DAY_CARD}`}>
      <DiaryDateRow date={entry.date} />

      {!hasLessons && !hasNotes && <DiaryEmptyDay />}

      {hasLessons && (
        <div className={`grid ${LESSON_COLUMNS}`}>
          <div className="h-12 bg-on-primary-surface" />
          {COLUMN_HEADER_KEYS.map((key) => (
            <div key={key} className={HEADER_CELL}>
              {t(key)}
            </div>
          ))}

          {entry.lessons.map((lesson, index) => {
            const [scheduledTest] = lesson.scheduledTests;

            return (
              <div
                key={`${lesson.className}-${index}`}
                className={`col-span-5 grid grid-cols-subgrid ${hasEmptySubject(lesson) ? '' : 'cursor-pointer transition-colors hover:bg-hover'}`}
                onClick={(event) => handleRowClick(event, lesson)}
              >
                <div
                  className={`flex items-center justify-center ${CELL_BORDER}`}
                >
                  <LessonNumber lessonNumber={lesson.lessonNumber} />
                </div>
                <div className={`flex ${CELL_BORDER}`}>
                  <DiaryLessonCell lesson={lesson} />
                </div>
                <div className={`flex ${CELL_BORDER}`}>
                  <DiaryTopics
                    topics={lesson.topics}
                    scheduledTest={scheduledTest}
                    className={TEXT_CELL_PADDING}
                  />
                </div>
                <div className={`flex ${CELL_BORDER}`}>
                  <DiaryHomeTasks
                    homeTasks={lesson.homeTasks}
                    thirdPartyEvents={lesson.thirdPartyEvents}
                    videoMeetings={lesson.videoMeetings}
                    className={TEXT_CELL_PADDING}
                  />
                </div>
                <div className="flex min-h-12 border-t border-border-table">
                  <DiaryEvaluations evaluations={lesson.evaluations} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasNotes && (
        <DiaryNotesRow
          behaviourEntries={entry.behaviourEntries}
          notes={entry.notes}
        />
      )}

      {openLesson && (
        <LessonDetailsModal
          mode={LessonDetailsMode.Lesson}
          lesson={openLesson}
          date={entry.date}
          onClose={() => setOpenLesson(undefined)}
        />
      )}
    </div>
  );
};
